import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/environment';
import { tokenStorage } from './storage';
import type { ApiEnvelope, NormalizedApiError } from '@/types/api';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/authStore';
import { decodeAccessTokenPermissions, decodeAccessTokenRoles } from '@/lib/jwt';

/**
 * apiClient.ts - Section 49.1's "one configured API client". Every feature
 * calls THIS instance, never a bare `axios.get(...)` - that's what gives us
 * a single place for auth headers, language headers, error normalization,
 * and refresh-token handling instead of repeating that logic per feature.
 */
export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 15_000,
});

/** The shape of the backend's ErrorResponse body (Section 39.1) - defined once and reused everywhere below, instead of two slightly different inline shapes. */
interface FieldErrorPayload {
  field: string;
  message: string;
}
type ErrorResponseBody = ApiEnvelope<unknown> & {
  error?: string;
  fieldErrors?: FieldErrorPayload[];
};

// ---------------- Request interceptor ----------------
apiClient.interceptors.request.use(async (config) => {
  let token = tokenStorage.getAccessToken();
  // After a page reload the in-memory access token is gone but the refresh token survives. Refresh
  // BEFORE sending, instead of sending the request bare, collecting a 403 and retrying - that cost
  // every hard reload a failed /users/me call (and a console error) on every portal page.
  if (!token && tokenStorage.getRefreshToken() && !config.url?.includes('/auth/')) {
    try {
      token = await sharedRefresh();
    } catch {
      // Refresh token rejected (expired/revoked): drop it and send the request as-is. A public
      // endpoint still answers; a protected one fails into the response interceptor below, which
      // finds no refresh token and sends the user to /login.
      tokenStorage.clearAll();
    }
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Backend messages/validation errors come back in whatever language this
  // header requests (Section 7.5) - i18next's current language IS the
  // single source of truth for both the UI strings and this header.
  config.headers['Accept-Language'] = i18n.language || env.defaultLanguage;
  return config;
});

// ---------------- Response interceptor: refresh-token rotation ----------------
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  // A separate, un-intercepted axios call - using `apiClient` here would
  // recurse back into this same interceptor.
  const response = await axios.post<ApiEnvelope<{ accessToken: string; refreshToken: string }>>(
    `${env.apiBaseUrl}/auth/refresh-token`,
    { refreshToken },
  );
  const { accessToken, refreshToken: newRefreshToken } = response.data.data;
  tokenStorage.setAccessToken(accessToken);
  tokenStorage.setRefreshToken(newRefreshToken);

  // Keep authStore's roles/permissions in sync with whatever this freshly-minted token actually
  // carries (RBAC->PBAC migration) - without this, a silent 401/403-triggered refresh here updates
  // the stored token but leaves the in-memory session's permissions stale until something else
  // happens to refetch /me, which could leave a staff member's UI showing yesterday's access for a
  // while after a role change even though the token itself is already current.
  const currentUser = useAuthStore.getState().currentUser;
  if (currentUser) {
    useAuthStore.getState().setCurrentUser({
      ...currentUser,
      roles: decodeAccessTokenRoles(accessToken),
      permissions: decodeAccessTokenPermissions(accessToken),
    });
  }

  return accessToken;
}

/** One refresh for every request that needs it at the same moment (refresh tokens rotate, so a
 *  second concurrent refresh with the same token would be rejected). */
function sharedRefresh(): Promise<string> {
  refreshInFlight =
    refreshInFlight ??
    refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorResponseBody>) => {
    const originalRequest = error.config as
      (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    // This backend has no custom AuthenticationEntryPoint, so Spring
    // Security's default returns 403 (not 401) for a request with NO token
    // at all - not just for "authenticated but wrong role". A page reload
    // wipes the in-memory access token (by design - see storage.ts), so the
    // very next request always looks like this "missing token" 403 until a
    // refresh happens. Treating 403 the same as 401 here is what lets a
    // reload recover the session via the refresh token instead of bouncing
    // straight to /login. Worst case for a genuine role-based 403 (valid
    // token, wrong role) is one wasted refresh+retry before it correctly
    // fails again - `_retry` still prevents any further looping.
    const isAuthFailure = error.response?.status === 401 || error.response?.status === 403;
    if (isAuthFailure && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await sharedRefresh();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch {
        tokenStorage.clearAll();
        // A hard redirect (not a router navigation) deliberately wipes all
        // in-memory React state too - Section 5.6: "clear sensitive state
        // during logout".
        window.location.assign('/login?sessionExpired=1');
        return Promise.reject(error);
      }
    }

    return Promise.reject(normalizeError(error));
  },
);

/** Turns any Axios error into the one predictable shape the rest of the app expects (Section 49.4). */
function normalizeError(error: AxiosError<ErrorResponseBody>): NormalizedApiError {
  const body = error.response?.data;
  return {
    message: body?.message ?? error.message ?? 'Something went wrong. Please try again.',
    errorCode: body?.error ?? 'UNKNOWN_ERROR',
    status: error.response?.status ?? 0,
    requestId: body?.requestId ?? 'unknown',
    fieldErrors: (body?.fieldErrors ?? []).map((f) => ({ field: f.field, message: f.message })),
  };
}
